import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
/* eslint-disable react-refresh/only-export-components */

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useChartSettings } from "../chartSettings/ChartSettingsContext";
import {
  kmToNm,
  nmToKm,
} from "../../i18n/unitFormat";

interface ReachableAreaContextValue {
  enabled: boolean;
  distanceNm: number;
  setEnabled: (v: boolean) => void;
  setDistanceNm: (v: number) => void;
  openDialog: () => void;
  closeDialog: () => void;
}

const Ctx = createContext<ReachableAreaContextValue | null>(null);

const STORAGE_ENABLED = "reachableArea.enabled";
const STORAGE_DISTANCE = "reachableArea.distanceNm";

const DEFAULT_DISTANCE_NM = 10;
const DISTANCE_MIN_NM = 0.1;
const DISTANCE_MAX_NM = 100;
const DISTANCE_STEP_NM = 0.1;

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function readBool(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    return v === "true";
  } catch {
    return fallback;
  }
}

function readNumber(key: string, fallback: number): number {
  try {
    const v = localStorage.getItem(key);
    if (!v) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

export function ReachableAreaProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { useSiUnits } = useChartSettings();

  const [enabled, _setEnabled] = useState<boolean>(() => readBool(STORAGE_ENABLED, false));
  const [distanceNm, _setDistanceNm] = useState<number>(() =>
    clamp(readNumber(STORAGE_DISTANCE, DEFAULT_DISTANCE_NM), DISTANCE_MIN_NM, 200)
  );

  const [dialogOpen, setDialogOpen] = useState(false);

  const setEnabled = useCallback((v: boolean) => {
    _setEnabled(Boolean(v));
  }, []);

  const setDistanceNm = useCallback((v: number) => {
    const next = clamp(Number(v), DISTANCE_MIN_NM, 200);
    _setDistanceNm(next);
  }, []);

  const openDialog = useCallback(() => setDialogOpen(true), []);
  const closeDialog = useCallback(() => setDialogOpen(false), []);

  const distanceDisplay = useSiUnits ? nmToKm(distanceNm) : distanceNm;
  const distanceMin = useSiUnits ? nmToKm(DISTANCE_MIN_NM) : DISTANCE_MIN_NM;
  const distanceMax = useSiUnits ? nmToKm(DISTANCE_MAX_NM) : DISTANCE_MAX_NM;
  const distanceStep = useSiUnits ? nmToKm(DISTANCE_STEP_NM) : DISTANCE_STEP_NM;

  const setDistanceFromDisplay = useCallback(
    (displayValue: number) => {
      const nm = useSiUnits ? kmToNm(displayValue) : displayValue;
      setDistanceNm(nm);
    },
    [setDistanceNm, useSiUnits],
  );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_ENABLED, String(enabled));
    } catch {
      // Ignore storage errors
    }
  }, [enabled]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_DISTANCE, String(distanceNm));
    } catch {
      // Ignore storage errors
    }
  }, [distanceNm]);

  const value = useMemo<ReachableAreaContextValue>(
    () => ({
      enabled,
      distanceNm,
      setEnabled,
      setDistanceNm,
      openDialog,
      closeDialog,
    }),
    [enabled, distanceNm, setEnabled, setDistanceNm, openDialog, closeDialog]
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <Dialog open={dialogOpen} onClose={closeDialog} fullScreen={fullScreen}>
        <DialogTitle>{t("reachableArea.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {t("reachableArea.distanceNm")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <Slider
                value={distanceDisplay}
                min={distanceMin}
                max={distanceMax}
                step={distanceStep}
                onChange={(_, v) =>
                  setDistanceFromDisplay(Array.isArray(v) ? v[0] : v)
                }
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                value={Number(distanceDisplay.toFixed(1))}
                onChange={(e) => setDistanceFromDisplay(Number(e.target.value))}
                inputProps={{ inputMode: "decimal" }}
                sx={{ width: 110 }}
                label={
                  useSiUnits ? t("reachableArea.km") : t("reachableArea.nm")
                }
              />
            </Box>
            <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
              {t("reachableArea.hint")}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>{t("common.close")}</Button>
        </DialogActions>
      </Dialog>
    </Ctx.Provider>
  );
}

export function useReachableArea() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useReachableArea must be used within ReachableAreaProvider");
  return ctx;
}
